import { getApiClient } from './client'
import {
  type SeriesResponseDto,
  type SeriesListResponseDto,
  type CreateSeriesDto,
  type UpdateSeriesDto,
  type UpdateSeriesPositionsDto,
  type SeriesMovePositionUpdateDto,
} from './generated/api'

// Re-export position-related types for convenience
export type { UpdateSeriesPositionsDto, SeriesMovePositionUpdateDto }

/**
 * Series API Facade
 *
 * Type-safe wrapper around the generated API client for series operations.
 * Provides consistent error handling and cleaner API for React Query hooks.
 */

/**
 * Fetch all series with optional filters
 *
 * @param params - Query parameters for filtering
 * @param params.searchText - Search series by name or description
 * @param params.limit - Maximum number of results (default: 20)
 * @param params.offset - Number of results to skip (default: 0)
 * @param params.includeRelations - Include related entities (moves, tags)
 * @returns Promise resolving to list of series with pagination metadata
 */
export async function fetchSeries(params?: {
  searchText?: string
  limit?: number
  offset?: number
  includeRelations?: boolean
}): Promise<SeriesListResponseDto> {
  const response = await getApiClient().api.seriesControllerFindAll({
    searchText: params?.searchText,
    limit: params?.limit,
    offset: params?.offset,
    includeRelations: params?.includeRelations,
  })

  return response.data
}

/**
 * Fetch a single series by ID
 *
 * @param id - Series ID (UUID)
 * @param includeRelations - Include related entities (moves, tags)
 * @returns Promise resolving to series details
 */
export async function fetchSeriesById(
  id: string,
  includeRelations: boolean = false
): Promise<SeriesResponseDto> {
  const response = await getApiClient().api.seriesControllerFindOne(id, {
    includeRelations,
  })

  return response.data
}

/**
 * Create a new series
 *
 * @param data - Series data to create
 * @returns Promise resolving to created series
 */
export async function createSeries(
  data: CreateSeriesDto
): Promise<SeriesResponseDto> {
  const response = await getApiClient().api.seriesControllerCreate(data)

  return response.data
}

/**
 * Update an existing series
 *
 * @param id - Series ID (UUID)
 * @param data - Partial series data to update
 * @returns Promise resolving to updated series
 */
export async function updateSeries(
  id: string,
  data: UpdateSeriesDto
): Promise<SeriesResponseDto> {
  const response = await getApiClient().api.seriesControllerUpdate(id, data)

  return response.data
}

/**
 * Delete a series (soft delete)
 *
 * @param id - Series ID (UUID)
 * @returns Promise resolving to void on success
 */
export async function deleteSeries(id: string): Promise<void> {
  await getApiClient().api.seriesControllerRemove(id)
}

/**
 * Update node positions for moves within a series
 *
 * @param seriesId - Series ID (UUID)
 * @param data - Position updates for series moves
 * @returns Promise resolving to updated series with new positions
 */
export async function updateSeriesPositions(
  seriesId: string,
  data: UpdateSeriesPositionsDto
): Promise<SeriesResponseDto> {
  const response = await getApiClient().api.seriesControllerUpdatePositions(
    seriesId,
    data
  )

  return response.data
}
